import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Button } from '../../ui/button';
import { Edit, Trash2, Eye, Trophy, EyeOff } from 'lucide-react';
import { useToast } from '../../ui/use-toast';
import { supabase } from '../../../lib/supabase';
import { Badge } from '../../ui/badge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useContestFilters } from '../../../stores/contestFilters';
import { Pagination } from '../../ui/pagination';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

interface Contest {
  id: string;
  name: string;
  description: string;
  status: string;
  start_date: string;
  end_date: string;
  vote_price: number;
  contestants_count?: number;
  votes_count?: number;
  slug: string;
  commission_percentage: number;
  logo_url?: string;
  is_public: boolean;
}

export const ContestTable = () => {
  const [contests, setContests] = useState<Contest[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { search, status, sortBy } = useContestFilters();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contestToDelete, setContestToDelete] = useState<Contest | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContests, setTotalContests] = useState(0);
  const ITEMS_PER_PAGE = 20;

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-yellow-400/20 text-yellow-400 border-yellow-400/20';
      case 'active':
        return 'bg-green-400/20 text-green-400 border-green-400/20';
      case 'ended':
        return 'bg-gray-400/20 text-gray-400 border-gray-400/20';
      default:
        return 'bg-white/20 text-white border-white/20';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return 'À venir';
      case 'active':
        return 'Actif';
      case 'ended':
      case 'completed':
        return 'Terminé';
      default:
        return status;
    }
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace('XAF', 'FCFA');
  };

  const formatCommission = (commission: number) => {
    return `${commission}%`;
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const viewPublicLink = (slug: string) => `/c/${slug}`;

  const fetchContests = async () => {
    try {
      // Fetch total count first
      let countQuery = supabase
        .from('contests')
        .select('*', { count: 'exact', head: true });

      if (status) {
        countQuery = countQuery.eq('status', status);
      }

      if (search) {
        countQuery = countQuery.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      const { count } = await countQuery;
      setTotalContests(count || 0);

      // Then fetch paginated data
      let query = supabase
        .from('contests')
        .select('*')
        .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

      if (status) {
        query = query.eq('status', status);
      }

      if (search) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      switch (sortBy) {
        case 'name':
          query = query.order('name');
          break;
        case 'start_date':
          query = query.order('start_date', { ascending: false });
          break;
        case 'contestants':
        case 'votes':
          // We'll sort these after fetching the counts
          break;
        default:
          query = query.order('created_at', { ascending: false });
      }

      const { data: contestsData, error: contestsError } = await query;

      if (contestsError) throw contestsError;

      // Get contestant and vote counts for each contest
      const contestsWithCounts = await Promise.all(
        (contestsData || []).map(async (contest) => {
          // Get contestant count
          const { count: contestantsCount } = await supabase
            .from('contestants')
            .select('id', { count: 'exact', head: true })
            .eq('contest_id', contest.id);

          // Get contestants for this contest
          const { data: contestants } = await supabase
            .from('contestants')
            .select('id')
            .eq('contest_id', contest.id);

          // Calculate total votes by summing votes for each contestant
          let totalVotes = 0;
          for (const contestant of contestants || []) {
            const { count: contestantVotes } = await supabase
              .from('votes')
              .select('id', { count: 'exact', head: true })
              .eq('contestant_id', contestant.id);
            
            totalVotes += contestantVotes || 0;
          }

          return {
            ...contest,
            contestants_count: contestantsCount || 0,
            votes_count: totalVotes
          };
        })
      );

      // Sort by contestant or vote count if needed
      if (sortBy === 'contestants') {
        contestsWithCounts.sort((a, b) => (b.contestants_count || 0) - (a.contestants_count || 0));
      } else if (sortBy === 'votes') {
        contestsWithCounts.sort((a, b) => (b.votes_count || 0) - (a.votes_count || 0));
      }

      setContests(contestsWithCounts);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Impossible de charger les concours'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contests')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      toast({
        title: 'Succès',
        description: 'Le concours a été supprimé'
      });

      setDeleteDialogOpen(false);
      setContestToDelete(null);
      fetchContests();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Impossible de supprimer le concours'
      });
    }
  };

  const togglePublicVisibility = async (contest: Contest) => {
    try {
      const newPublicStatus = !contest.is_public;
      
      const { error } = await supabase
        .from('contests')
        .update({ is_public: newPublicStatus })
        .eq('id', contest.id);

      if (error) {
        throw error;
      }

      toast({
        title: 'Succès',
        description: `Le concours est maintenant ${newPublicStatus ? 'visible' : 'invisible'} au public`
      });

      // Mettre à jour l'état local
      setContests(contests.map(c => 
        c.id === contest.id ? { ...c, is_public: newPublicStatus } : c
      ));
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Impossible de modifier la visibilité du concours'
      });
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [search, status, sortBy]);

  useEffect(() => {
    fetchContests();
  }, [currentPage, search, status, sortBy]);

  if (loading) {
    return <div className="text-center p-4">Chargement...</div>;
  }

  return (
    <div className="w-full">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-white/10 hover:bg-white/5">
            <TableHead className="text-gray-400 font-medium">Logo</TableHead>
            <TableHead className="text-gray-400 font-medium">Nom</TableHead>
            <TableHead className="text-gray-400 font-medium">Statut</TableHead>
            <TableHead className="text-gray-400 font-medium">Candidats</TableHead>
            <TableHead className="text-gray-400 font-medium">Votes</TableHead>
            <TableHead className="text-gray-400 font-medium">Commission</TableHead>
            <TableHead className="text-gray-400 font-medium">Début</TableHead>
            <TableHead className="text-gray-400 font-medium">Fin</TableHead>
            <TableHead className="text-gray-400 font-medium">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contests.map((contest, index) => (
            <TableRow 
              key={contest.id}
              className={`border-b border-white/10 hover:bg-white/10 transition-colors ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}
            >
              <TableCell>
                <div className="relative w-10 h-10 rounded-lg overflow-hidden bg-white/5 border border-white/10">
                  {contest.logo_url ? (
                    <img
                      src={contest.logo_url}
                      alt={`Logo ${contest.name}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Trophy className="w-6 h-6 text-gray-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  )}
                </div>
              </TableCell>
              <TableCell className="font-medium text-white">
                {contest.name}
              </TableCell>
              <TableCell>
                <Badge className={`${getStatusBadgeVariant(contest.status)} px-2 py-1 text-xs rounded-lg border`}>
                  {getStatusLabel(contest.status)}
                </Badge>
              </TableCell>
              <TableCell className="text-gray-300">
                {contest.contestants_count} candidat{contest.contestants_count !== 1 ? 's' : ''}
              </TableCell>
              <TableCell className="text-gray-300">
                {formatNumber(contest.votes_count || 0)} vote{(contest.votes_count || 0) !== 1 ? 's' : ''}
              </TableCell>
              <TableCell className="text-gray-300">
                {formatCommission(contest.commission_percentage)}
              </TableCell>
              <TableCell className="text-gray-300">
                {format(new Date(contest.start_date), 'dd MMM yyyy', { locale: fr })}
              </TableCell>
              <TableCell className="text-gray-300">
                {format(new Date(contest.end_date), 'dd MMM yyyy', { locale: fr })}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => togglePublicVisibility(contest)}
                          className={`h-8 w-8 rounded-lg ${contest.is_public ? 'bg-green-500/20 hover:bg-green-500/30' : 'bg-red-500/20 hover:bg-red-500/30'} transition-colors flex items-center justify-center ${contest.is_public ? 'text-green-400' : 'text-red-400'} hover:text-white`}
                        >
                          {contest.is_public ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{contest.is_public ? 'Masquer du public' : 'Rendre visible au public'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <Link
                    to={viewPublicLink(contest.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center text-gray-400 hover:text-white"
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                  <Link
                    to={`/admin/contests/edit/${contest.id}`}
                    className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center text-gray-400 hover:text-white"
                  >
                    <Edit className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() => {
                      setContestToDelete(contest);
                      setDeleteDialogOpen(true);
                    }}
                    className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center text-gray-400 hover:text-white"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalContests / ITEMS_PER_PAGE)}
        onPageChange={(page) => setCurrentPage(page)}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer le concours "{contestToDelete?.name}" ?
              Cette action supprimera également tous les participants et les votes associés.
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => contestToDelete && handleDelete(contestToDelete.id)}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};